export const SUBJECT_KINDS = ['person', 'organization'] as const;
export type SubjectKind = (typeof SUBJECT_KINDS)[number];

export const PD_ENTITY_TYPES = ['person', 'organization', 'deal', 'activity', 'note', 'user'] as const;
export type PdEntityType = (typeof PD_ENTITY_TYPES)[number];

export const SYNC_RUN_KINDS = ['full', 'delta', 'backup'] as const;
export type SyncRunKind = (typeof SYNC_RUN_KINDS)[number];

export const RUN_STATUSES = ['queued', 'running', 'completed', 'failed'] as const;
export type RunStatus = (typeof RUN_STATUSES)[number];

export const MATCH_TIERS = ['auto', 'review'] as const;
export type MatchTier = (typeof MATCH_TIERS)[number];

export const PAIR_STATUSES = ['pending', 'approved', 'declined', 'merged', 'deleted', 'stale', 'suppressed'] as const;
export type PairStatus = (typeof PAIR_STATUSES)[number];

export const CLUSTER_STATUSES = ['pending', 'approved', 'declined', 'merging', 'merged', 'deleted', 'stale'] as const;
export type ClusterStatus = (typeof CLUSTER_STATUSES)[number];

export const REVIEW_DECISIONS = ['approve_merge', 'approve_delete', 'not_duplicate', 'defer', 'reopen'] as const;
export type ReviewDecision = (typeof REVIEW_DECISIONS)[number];

/**
 * What an approved cluster asks the merge job to do: `merge` the members into a survivor, or
 * `delete` every member (only when none carries deals or linked persons). Also the merge task's action.
 */
export const CLUSTER_RESOLUTIONS = ['merge', 'delete'] as const;
export type ClusterResolution = (typeof CLUSTER_RESOLUTIONS)[number];

export const MERGE_JOB_MODES = ['dry_run', 'pilot', 'full'] as const;
export type MergeJobMode = (typeof MERGE_JOB_MODES)[number];

export const MERGE_JOB_STATUSES = ['queued', 'running', 'paused', 'completed', 'failed', 'cancelled'] as const;
export type MergeJobStatus = (typeof MERGE_JOB_STATUSES)[number];

export const MERGE_TASK_STATUSES = [
  'pending',
  'verifying',
  'patched',
  'merging',
  'merged',
  'deleting',
  'verified',
  'failed',
  'skipped_stale',
] as const;
export type MergeTaskStatus = (typeof MERGE_TASK_STATUSES)[number];

export const CONSENT_STATUSES = ['granted', 'denied', 'withdrawn', 'unknown'] as const;
export type ConsentStatus = (typeof CONSENT_STATUSES)[number];

export const BLOCK_KEY_TYPES = ['phone', 'email', 'email_alias', 'ico', 'name_exact'] as const;
export type BlockKeyType = (typeof BLOCK_KEY_TYPES)[number];

export const ENRICHMENT_MATCH_STATUSES = ['auto_accepted', 'pending', 'accepted', 'rejected', 'no_match'] as const;
export type EnrichmentMatchStatus = (typeof ENRICHMENT_MATCH_STATUSES)[number];

export const ENRICHMENT_PROVIDERS = ['ares', 'apollo', 'lusha'] as const;
export type EnrichmentProviderName = (typeof ENRICHMENT_PROVIDERS)[number];

/** Canonical org firmographic fields Apollo/Lusha can contribute (enrichment_value.field). */
export const ORG_FIRMOGRAPHIC_FIELDS = [
  'website',
  'linkedin',
  'industry',
  'employee_count',
  'annual_revenue',
  'phone',
  'address',
  'founded_year',
  'description',
] as const;
export type OrgFirmographicField = (typeof ORG_FIRMOGRAPHIC_FIELDS)[number];

/** Any endpoint value shared by more than this many records is treated as a
 *  shared office endpoint (reception phone, info@ mailbox) and can never
 *  contribute auto-merge evidence. */
export const SHARED_ENDPOINT_THRESHOLD = 5;

/** Clusters are auto-tier only when fully auto-connected AND at most this large. */
export const AUTO_CLUSTER_MAX_SIZE = 3;

export const UNIT_AVAILABILITIES = ['available', 'reserved', 'sold'] as const;
export type UnitAvailability = (typeof UNIT_AVAILABILITIES)[number];

export const SITE_CHANGE_KINDS = ['price', 'availability', 'unit_added', 'unit_removed'] as const;
export type SiteChangeKind = (typeof SITE_CHANGE_KINDS)[number];

export const SITE_CHANGE_STATUSES = ['pending', 'applied', 'dismissed'] as const;
export type SiteChangeStatus = (typeof SITE_CHANGE_STATUSES)[number];

export const BLUEPRINT_STATUSES = ['active', 'broken', 'superseded'] as const;
export type BlueprintStatus = (typeof BLUEPRINT_STATUSES)[number];

/** Real-estate project "distance to" fields (travel time in minutes). */
export const PROJECT_DISTANCE_KEYS = [
  'minutesShops',
  'minutesRestaurant',
  'minutesPublicTransport',
  'minutesSchool',
  'minutesHospital',
  'minutesSport',
] as const;
export type ProjectDistanceKey = (typeof PROJECT_DISTANCE_KEYS)[number];

/** What started a run: a person in the UI, the job scheduler, or another run's completion (system). */
export const RUN_TRIGGERS = ['user', 'schedule', 'system'] as const;
export type RunTrigger = (typeof RUN_TRIGGERS)[number];
